import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Table,
  Typography,
  Space,
  Button,
  App,
  Statistic,
  Row,
  Col,
  Select,
  Modal,
  DatePicker,
  Popconfirm,
  Form,
  InputNumber,
  Switch,
  Drawer,
  Tag,
  Progress,
  List,
  Divider,
  Spin,
  Empty,
} from 'antd';
import { useAuth } from '../context/AuthContext';
import {
  getAdminCostSummary,
  getAdminCostProjectDetail,
  updateAdminProjectBudget,
  getAdminBudgetAlerts,
  getAdminGuardrailAudit,
  clearAdminGuardrailAudit,
  getAdminMyGroupsBudgetPool,
} from '../services/api';
import { formatReadableProjectId } from '../utils/helpers';
import { confirmDelete } from '../utils/notifications';
import { useThemeStyles } from '../context/ThemeContext';
import PageHeader from '../components/PageHeader';
import { pageShellStyle } from '../theme';

const { Text } = Typography;

const formatToIST = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(date);
};

function AdminCostMonitoring() {
  const { message } = App.useApp();
  const { token, isAdmin, isSuperAdmin } = useAuth();
  const isGroupAdmin = isAdmin && !isSuperAdmin;
  const themeStyles = useThemeStyles();
  const isLight = themeStyles.isLight;
  const [days, setDays] = useState(30);

  const [detailDays, setDetailDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [budgetModalOpen, setBudgetModalOpen] = useState(false);
  const [budgetProject, setBudgetProject] = useState(null);
  const [budgetSubmitting, setBudgetSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detailProjectId, setDetailProjectId] = useState(null);
  const [detailProjectLabel, setDetailProjectLabel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // Guardrail audit table: blocked uncensored model output captured for admin review.
  const [auditRows, setAuditRows] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditProjectId, setAuditProjectId] = useState(null);
  const [auditPreviewOpen, setAuditPreviewOpen] = useState(false);
  const [selectedAuditRow, setSelectedAuditRow] = useState(null);
  const [clearModalOpen, setClearModalOpen] = useState(false);
  const [clearSubmitting, setClearSubmitting] = useState(false);
  const [clearAction, setClearAction] = useState('today');
  const [clearDay, setClearDay] = useState(null);
  const [clearRange, setClearRange] = useState(null);
  const [groupPools, setGroupPools] = useState([]);

  const loadGroupPools = useCallback(async () => {
    if (!token || !isGroupAdmin) return;
    try {
      const res = await getAdminMyGroupsBudgetPool(token, days);
      setGroupPools(Array.isArray(res?.data?.groups) ? res.data.groups : []);
    } catch {
      setGroupPools([]);
    }
  }, [token, isGroupAdmin, days]);

  const maxProjectCapForRow = useCallback(
    (row) => {
      if (!row?.group_id || !isGroupAdmin) return null;
      const pool = groupPools.find((g) => g.group_id === row.group_id);
      if (!pool || pool.group_budget_usd == null) return null;
      const remaining = Number(pool.remaining_unallocated_usd ?? 0);
      const current = Number(row.budget?.monthly_limit_usd ?? 0);
      return remaining + current;
    },
    [groupPools, isGroupAdmin],
  );

  const loadSummary = useCallback(async () => {
    if (!token || !isAdmin) return;
    setLoading(true);
    try {
      const res = await getAdminCostSummary(token, days);
      setSummary(res.data || null);
    } catch (e) {
      message.error(e.response?.data?.detail || 'Failed to load cost summary');
    } finally {
      setLoading(false);
    }
  }, [token, isAdmin, days, message]);

  const loadAlerts = useCallback(async () => {
    if (!token || !isAdmin) return;
    try {
      const res = await getAdminBudgetAlerts(token, 80);
      setAlerts(res.data?.alerts || []);
    } catch (e) {
      message.error(e.response?.data?.detail || 'Failed to load alerts');
    }
  }, [token, isAdmin, message]);

  // Loads recent guardrail audit rows; optional project filter and fixed page size.
  const loadAudit = useCallback(async () => {
    if (!token || !isAdmin) return;
    setAuditLoading(true);
    try {
      const res = await getAdminGuardrailAudit(token, auditProjectId, 40);
      setAuditRows(Array.isArray(res.data?.records) ? res.data.records : []);
    } catch (e) {
      message.error(e.response?.data?.detail || 'Failed to load guardrail audit');
    } finally {
      setAuditLoading(false);
    }
  }, [token, isAdmin, auditProjectId, message]);

  // Bulk or single-record delete for guardrail audit history.
  const runClearAudit = async (payload, successMessage = 'Guardrail audit history cleared') => {
    setClearSubmitting(true);
    try {
      const res = await clearAdminGuardrailAudit(token, payload);
      message.success(`${successMessage} (${res.data?.deleted_count ?? 0} deleted)`);
      setClearModalOpen(false);
      await loadAudit();
    } catch (e) {
      message.error(e.response?.data?.detail || 'Failed to clear guardrail audit history');
    } finally {
      setClearSubmitting(false);
    }
  };

  useEffect(() => {
    loadSummary();
    loadAlerts();
    loadAudit();
    loadGroupPools();
  }, [loadSummary, loadAlerts, loadAudit, loadGroupPools]);

  const openBudgetModal = (row) => {
    setBudgetProject(row);
    form.setFieldsValue({
      monthly_limit_usd: row.budget?.monthly_limit_usd ?? null,
      alert_threshold_pct: row.budget?.alert_threshold_pct ?? 80,
      block_when_exceeded: row.budget?.block_when_exceeded ?? false,
    });
    setBudgetModalOpen(true);
  };

  const submitBudget = async () => {
    const v = await form.validateFields();
    setBudgetSubmitting(true);
    try {
      await updateAdminProjectBudget(token, budgetProject.project_id, {
        monthly_limit_usd:
          v.monthly_limit_usd === undefined || v.monthly_limit_usd === null
            ? null
            : Number(v.monthly_limit_usd),
        alert_threshold_pct: Number(v.alert_threshold_pct),
        block_when_exceeded: !!v.block_when_exceeded,
      });
      message.success('Budget updated');
      setBudgetModalOpen(false);
      loadSummary();
      loadAlerts();
      loadGroupPools();
    } catch (e) {
      message.error(e.response?.data?.detail || 'Failed to update budget');
    } finally {
      setBudgetSubmitting(false);
    }
  };

  const loadProjectDetail = useCallback(async (projectId) => {
    if (!token || !isAdmin || !projectId) return;
    setDetailLoading(true);
    try {
      const res = await getAdminCostProjectDetail(token, projectId, detailDays);
      setDetail(res.data);
    } catch (e) {
      message.error(e.response?.data?.detail || 'Failed to load project usage');
    } finally {
      setDetailLoading(false);
    }
  }, [token, isAdmin, detailDays, message]);

  useEffect(() => {
    if (!drawerOpen || !detailProjectId) return;
    loadProjectDetail(detailProjectId);
  }, [drawerOpen, detailProjectId, detailDays, loadProjectDetail]);

  // Move global message / toast stack to the right while the usage drawer is open (drawer is on the right).
  useEffect(() => {
    if (!drawerOpen) return undefined;
    document.body.classList.add('tf-cost-usage-drawer-open');
    return () => {
      document.body.classList.remove('tf-cost-usage-drawer-open');
    };
  }, [drawerOpen]);

  const openDetail = (projectId, projectName) => {
    setDetailProjectId(projectId);
    setDetailProjectLabel(projectName || null);
    setDetailDays(days);
    setDetail(null);
    setDrawerOpen(true);
  };

  const projectNameById = useMemo(() => {
    const map = new Map();
    for (const p of summary?.projects || []) {
      if (p?.project_id && p?.name) map.set(p.project_id, p.name);
    }
    return map;
  }, [summary]);

  if (!isAdmin) {
    return (
      <Card style={{ margin: 24, background: themeStyles.cardBg }}>
        <Text style={{ color: themeStyles.textSecondary }}>Admin or super admin access required.</Text>
      </Card>
    );
  }

  const projects = summary?.projects || [];
  const totalUsd = summary?.total_usd ?? 0;
  const budgetProjectMaxCap = budgetProject ? maxProjectCapForRow(budgetProject) : null;
  const detailProjectName =
    detail?.name
    || detailProjectLabel
    || projects.find((project) => project.project_id === detailProjectId)?.name
    || null;
  const detailProjectIdDisplay = detail?.project_id || detailProjectId;
  const detailDrawerTitle = (() => {
    const readableId = detailProjectIdDisplay
      ? formatReadableProjectId(detailProjectIdDisplay)
      : null;
    if (detailProjectName && readableId) {
      return `${detailProjectName}(${readableId})`;
    }
    if (detailProjectName) return detailProjectName;
    if (readableId) return readableId;
    return 'Project usage';
  })();
  const dailyUsageRows = detail?.daily_usage || [];
  const sourceUsageRows = Object.entries(detail?.aggregate?.by_source || {}).map(([source, stats]) => {
    if (typeof stats === 'number') {
      return { source, cost_usd: stats, prompt_tokens: 0, completion_tokens: 0, embedding_tokens: 0, events: 0 };
    }
    return { source, ...stats };
  });
  const dailyUsageColumns = [
    {
      title: 'Day (IST)',
      dataIndex: 'day',
      key: 'day',
      width: 112,
    },
    {
      title: 'Events',
      dataIndex: 'events',
      key: 'events',
      width: 72,
    },
    {
      title: 'Cost',
      dataIndex: 'cost_usd',
      key: 'cost_usd',
      width: 108,
      render: (value) => <Text style={{ color: themeStyles.textPrimary }}>${Number(value || 0).toFixed(6)}</Text>,
    },
    {
      title: 'Prompt',
      dataIndex: 'prompt_tokens',
      key: 'prompt_tokens',
      width: 88,
    },
    {
      title: 'Completion',
      dataIndex: 'completion_tokens',
      key: 'completion_tokens',
      width: 96,
    },
    {
      title: 'Embedding',
      dataIndex: 'embedding_tokens',
      key: 'embedding_tokens',
      width: 92,
    },
  ];

  const columns = [
    {
      title: 'Project',
      key: 'name',
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ color: themeStyles.textPrimary }}>
            {row.name || row.project_id}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <span title={row.project_id}>
              {formatReadableProjectId(row.project_id)}
            </span>
          </Text>
        </Space>
      ),
    },
    {
      title: 'Group',
      key: 'group',
      width: 160,
      render: (_, row) => {
        const label = row.group_name || row.group_id;
        if (!label) return <Text type="secondary">—</Text>;
        return (
          <Text style={{ color: themeStyles.textPrimary }} title={row.group_id || undefined}>
            {label}
          </Text>
        );
      },
    },
    {
      title: `Spend (${days}d)`,
      dataIndex: 'spend_usd',
      key: 'spend_usd',
      width: 120,
      render: (v) => <Text style={{ color: themeStyles.textPrimary }}>${Number(v || 0).toFixed(4)}</Text>,
    },
    {
      title: 'Budget limit',
      key: 'limit',
      width: 120,
      render: (_, row) => {
        const lim = row.budget?.monthly_limit_usd;
        return lim == null ? <Text type="secondary">—</Text> : <Text style={{ color: themeStyles.textPrimary }}>${Number(lim).toFixed(2)}</Text>;
      },
    },
    {
      title: 'Utilization',
      key: 'util',
      width: 200,
      render: (_, row) => {
        const pct = row.utilization_pct;
        if (pct == null) return <Text type="secondary">—</Text>;
        const p = Math.min(100, Number(pct));
        const status = p >= 100 ? 'exception' : p >= (row.budget?.alert_threshold_pct || 80) ? 'active' : 'normal';
        return <Progress percent={Math.round(p * 10) / 10} size="small" status={status} />;
      },
    },
    {
      title: 'Events',
      dataIndex: 'events',
      width: 80,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, row) => (
        <Space>
          <Button type="link" size="small" onClick={() => openDetail(row.project_id, row.name)}>
            Details
          </Button>
          {isGroupAdmin ? (
            <Button type="link" size="small" onClick={() => openBudgetModal(row)}>
              Budget
            </Button>
          ) : null}
        </Space>
      ),
    },
  ];

  const kpiCardStyle = isLight
    ? {
        background: '#F3F4F6',
        border: '1px solid #D1D5DB',
        borderRadius: 10,
      }
    : {
        background: themeStyles.elevatedBg,
        border: `1px solid ${themeStyles.borderColor}`,
        borderRadius: 10,
      };

  const sectionCardShell = {
    marginBottom: 16,
    background: themeStyles.cardBg,
    ...(isLight
      ? {
          border: '1px solid #D1D5DB',
          borderRadius: 10,
        }
      : {}),
  };

  return (
    <div
      style={{
        ...pageShellStyle,
        paddingBottom: 56,
        maxWidth: 1400,
        margin: '0 auto',
        minHeight: '100%',
      }}
    >
      <PageHeader
        title="Cost & usage"
        actions={(
          <Select
            value={days}
            onChange={setDays}
            style={{ width: 160, minWidth: 160 }}
            options={[
              { label: 'Last 7 days', value: 7 },
              { label: 'Last 30 days', value: 30 },
              { label: 'Last 90 days', value: 90 },
            ]}
          />
        )}
      />

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={8}>
          <Card style={kpiCardStyle}>
            <Statistic
              title="Total estimated spend (window)"
              value={totalUsd}
              precision={4}
              prefix="$"
              valueStyle={{ color: themeStyles.textPrimary }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card style={kpiCardStyle}>
            <Statistic
              title="Projects with usage or budget"
              value={projects.length}
              valueStyle={{ color: themeStyles.textPrimary }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card style={kpiCardStyle}>
            <Statistic
              title="Recent budget alerts"
              value={alerts.length}
              valueStyle={{ color: themeStyles.textPrimary }}
            />
          </Card>
        </Col>
      </Row>

      <div style={{ marginBottom: 16 }}>
        <Text
          strong
          style={{
            color: themeStyles.textPrimary,
            fontSize: 16,
            display: 'block',
            marginBottom: 12,
          }}
        >
          Projects
        </Text>
        <Table
          rowKey="project_id"
          loading={loading}
          dataSource={projects}
          columns={columns}
          pagination={{
            pageSize: 5,
            showSizeChanger: false,
            hideOnSinglePage: false,
          }}
        />
      </div>

      <div style={{ paddingBottom: 20 }}>
        <Card
          title="Budget alerts"
          style={{ ...sectionCardShell, marginBottom: 0 }}
          styles={{
            header: { color: themeStyles.textPrimary },
            body: { padding: alerts.length === 0 ? '20px 24px' : '8px 16px 12px' },
          }}
        >
          {alerts.length === 0 ? (
            <Text type="secondary">No alerts yet. Alerts fire when spend crosses the configured threshold or 100% of budget.</Text>
          ) : (
            <List
              size="small"
              split
              dataSource={alerts.slice(0, 40)}
              renderItem={(item) => (
                <List.Item
                  style={{
                    padding: '10px 8px',
                    alignItems: 'flex-start',
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 6,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        columnGap: 10,
                        rowGap: 4,
                      }}
                    >
                      <Tag
                        color={
                          item.alert_type === 'budget_exceeded' || item.alert_type === 'group_budget_exceeded'
                            ? 'red'
                            : 'orange'
                        }
                      >
                        {item.alert_type}
                      </Tag>
                      <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.45 }}>
                        {formatToIST(item.created_at)} IST
                      </Text>
                      {item.scope === 'group' || item.group_id ? (
                        <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.45 }}>
                          group: <span title={item.group_id || ''}>{item.group_name || item.group_id || '—'}</span>
                        </Text>
                      ) : (
                        <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.45 }}>
                          project:{' '}
                          <span title={item.project_id || ''}>
                            {projectNameById.get(item.project_id) || formatReadableProjectId(item.project_id)}
                          </span>
                        </Text>
                      )}
                    </div>
                    <Text
                      style={{
                        color: themeStyles.textPrimary,
                        lineHeight: 1.5,
                        fontSize: 13,
                        display: 'block',
                      }}
                    >
                      {item.message}
                    </Text>
                  </div>
                </List.Item>
              )}
            />
          )}
        </Card>
      </div>

      {/* Admin review of blocked uncensored model output per project/query. */}
     

      {/* Bulk delete audit history by scope, day, or date range. */}
      <Modal
        title="Clear guardrail audit history"
        open={clearModalOpen}
        onCancel={() => setClearModalOpen(false)}
        confirmLoading={clearSubmitting}
        onOk={async () => {
          const payload = { action: clearAction };
          if (auditProjectId) payload.project_id = auditProjectId;
          if (clearAction === 'day') {
            if (!clearDay) {
              message.error('Please select a day');
              return;
            }
            payload.day = clearDay.format('YYYY-MM-DD');
          } else if (clearAction === 'range') {
            if (!clearRange || clearRange.length !== 2) {
              message.error('Please select a date range');
              return;
            }
            payload.start_day = clearRange[0].format('YYYY-MM-DD');
            payload.end_day = clearRange[1].format('YYYY-MM-DD');
          }
          await runClearAudit(payload);
        }}
        okButtonProps={{ danger: true }}
        okText="Delete"
        destroyOnClose
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Text type="secondary">
            Scope: {auditProjectId ? `project ${formatReadableProjectId(auditProjectId)}` : 'all visible projects'}
          </Text>
          <Select
            value={clearAction}
            onChange={setClearAction}
            options={[
              { value: 'all', label: 'Delete all history' },
              { value: 'today', label: 'Delete today (IST)' },
              { value: 'yesterday', label: 'Delete yesterday (IST)' },
              { value: 'day', label: 'Delete specific day (IST)' },
              { value: 'range', label: 'Delete date range (IST)' },
            ]}
          />
          {clearAction === 'day' ? (
            <DatePicker
              value={clearDay}
              onChange={(v) => setClearDay(v)}
              style={{ width: '100%' }}
              format="YYYY-MM-DD"
            />
          ) : null}
          {clearAction === 'range' ? (
            <DatePicker.RangePicker
              value={clearRange}
              onChange={(v) => setClearRange(v)}
              style={{ width: '100%' }}
              format="YYYY-MM-DD"
            />
          ) : null}
          <Text type="danger">
            This permanently deletes matching audit records.
          </Text>
        </Space>
      </Modal>

      {/* Full uncensored output and guardrail diagnostics for one audit row. */}
      <Modal
        title="Uncensored output details"
        open={auditPreviewOpen}
        onCancel={() => {
          setAuditPreviewOpen(false);
          setSelectedAuditRow(null);
        }}
        footer={null}
        width={900}
        destroyOnClose
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Text type="secondary">Project</Text>
            <div>
              <Text style={{ fontFamily: 'monospace' }}>
                {selectedAuditRow?.project_id ? formatReadableProjectId(selectedAuditRow.project_id) : '—'}
              </Text>
            </div>
            {selectedAuditRow?.project_id ? (
              <div style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontFamily: 'monospace' }}>
                  {selectedAuditRow.project_id}
                </Text>
              </div>
            ) : null}
          </div>
          <div>
            <Text type="secondary">When</Text>
            <div><Text>{formatToIST(selectedAuditRow?.created_at)} IST</Text></div>
          </div>
          <div>
            <Text type="secondary">Query</Text>
            <div
              style={{
                marginTop: 6,
                padding: 10,
                borderRadius: 8,
                background: 'rgba(255,255,255,0.04)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              <Text style={{ color: themeStyles.textPrimary }}>
                {selectedAuditRow?.query || '—'}
              </Text>
            </div>
          </div>
          <div>
            <Text type="secondary">Uncensored output</Text>
            <div
              style={{
                marginTop: 6,
                maxHeight: 420,
                overflow: 'auto',
                padding: 10,
                borderRadius: 8,
                background: 'rgba(255,255,255,0.04)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              <Text style={{ color: themeStyles.textPrimary }}>
                {selectedAuditRow?.raw_answer || '—'}
              </Text>
            </div>
          </div>
          <div>
            <Text type="secondary">Guardrails applied</Text>
            <div style={{ marginTop: 6 }}>
              {(selectedAuditRow?.applied_guards || []).length ? (
                <Space wrap>
                  {(selectedAuditRow?.applied_guards || []).map((g) => (
                    <Tag key={`applied-${g}`} color="blue">{g}</Tag>
                  ))}
                </Space>
              ) : (
                <Text>—</Text>
              )}
            </div>
          </div>
          <div>
            <Text type="secondary">Blocked by</Text>
            <div style={{ marginTop: 6 }}>
              {(selectedAuditRow?.blocked_guards || []).length ? (
                <Space wrap>
                  {(selectedAuditRow?.blocked_guards || []).map((g) => (
                    <Tag key={`blocked-${g}`} color="red">{g}</Tag>
                  ))}
                </Space>
              ) : (
                <Text>—</Text>
              )}
            </div>
          </div>
          <div>
            <Text type="secondary">Why blocked (detected types)</Text>
            <div style={{ marginTop: 6 }}>
              {(() => {
                const diagnostics = selectedAuditRow?.guard_diagnostics || {};
                const entries = Object.entries(diagnostics);
                if (!entries.length) return <Text>—</Text>;
                return (
                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    {entries.map(([guardName, d]) => {
                      const entities = Array.isArray(d?.detected_entities) ? d.detected_entities : [];
                      const summaries = Array.isArray(d?.validation_summaries) ? d.validation_summaries : [];
                      const entityTypes = [...new Set(
                        entities
                          .map((e) => String(e?.entity || '').trim())
                          .filter(Boolean)
                      )];
                      const failureReason = summaries
                        .map((s) => s?.failureReason)
                        .find((r) => r && String(r).trim());
                      return (
                        <div key={`diag-${guardName}`}>
                          <Space wrap>
                            <Tag color="purple">{guardName}</Tag>
                            {entityTypes.length
                              ? entityTypes.map((t) => <Tag key={`${guardName}-${t}`}>{t}</Tag>)
                              : <Tag>no entity type info</Tag>}
                          </Space>
                          {failureReason ? (
                            <div style={{ marginTop: 4 }}>
                              <Text type="secondary">{String(failureReason)}</Text>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </Space>
                );
              })()}
            </div>
          </div>
          <div>
            <Text type="secondary">Returned response (to user)</Text>
            <div
              style={{
                marginTop: 6,
                padding: 10,
                borderRadius: 8,
                background: 'rgba(255,255,255,0.04)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              <Text style={{ color: themeStyles.textPrimary }}>
                {selectedAuditRow?.returned_response || '—'}
              </Text>
            </div>
          </div>
        </Space>
      </Modal>

      <Modal
        title={budgetProject ? `Budget — ${budgetProject.name || budgetProject.project_id}` : 'Budget'}
        open={budgetModalOpen}
        onCancel={() => setBudgetModalOpen(false)}
        onOk={submitBudget}
        confirmLoading={budgetSubmitting}
        okText="Save"
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="monthly_limit_usd"
            label="Rolling budget (USD)"
            extra={
              budgetProjectMaxCap != null
                ? `Leave empty for no limit. Maximum for this project within your group pool: $${budgetProjectMaxCap.toFixed(4)}.`
                : 'Leave empty for no limit. Compared to spend over the same window as the dashboard (e.g. 30 days).'
            }
            rules={[
              {
                validator: (_, value) => {
                  if (value === null || value === undefined || value === '') return Promise.resolve();
                  const max = maxProjectCapForRow(budgetProject);
                  if (max != null && Number(value) > max + 1e-9) {
                    return Promise.reject(
                      new Error(`Cannot exceed group pool ($${max.toFixed(4)} max for this project).`),
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              min={0}
              max={budgetProjectMaxCap != null ? budgetProjectMaxCap : undefined}
              step={0.01}
              style={{ width: '100%' }}
              placeholder="No cap"
            />
          </Form.Item>
          <Form.Item name="alert_threshold_pct" label="Alert threshold (% of budget)" rules={[{ required: true }]}>
            <InputNumber min={1} max={100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="block_when_exceeded" label="Block chat & embeddings when over budget" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={detailDrawerTitle}
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setDetailProjectId(null);
          setDetailProjectLabel(null);
          setDetail(null);
        }}
        width={720}
        destroyOnClose
        styles={{
          header: {
            padding: '18px 24px',
            borderBottom: `1px solid ${themeStyles.borderColor}`,
            color: themeStyles.textPrimary,
          },
          body: {
            padding: '20px 24px 28px',
            background: themeStyles.contentBg,
          },
        }}
      >
        {detailLoading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 220,
              padding: 32,
            }}
          >
            <Spin size="large" tip="Loading usage…" />
          </div>
        ) : detail ? (
          <Space direction="vertical" size={28} style={{ width: '100%' }}>
            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  marginBottom: 14,
                }}
              >
                <Select
                  value={detailDays}
                  onChange={setDetailDays}
                  style={{ width: 160, minWidth: 160, flexShrink: 0 }}
                  options={[
                    { label: 'Last 7 days', value: 7 },
                    { label: 'Last 30 days', value: 30 },
                    { label: 'Last 90 days', value: 90 },
                  ]}
                />
              </div>
              <div
                style={{
                  padding: '18px 18px 16px',
                  borderRadius: 10,
                  border: `1px solid ${themeStyles.borderColor}`,
                  background: 'rgba(255, 255, 255, 0.03)',
                }}
              >
                <Row gutter={[16, 16]}>
                  <Col xs={12} sm={8}>
                    <Statistic
                      title={<span style={{ color: themeStyles.textSecondary }}>Estimated cost</span>}
                      value={Number(detail.aggregate?.total_usd || 0)}
                      precision={6}
                      prefix="$"
                      valueStyle={{ color: themeStyles.textPrimary, fontSize: 18 }}
                    />
                  </Col>
                  <Col xs={12} sm={8}>
                    <Statistic
                      title={<span style={{ color: themeStyles.textSecondary }}>Events</span>}
                      value={detail.aggregate?.events || 0}
                      valueStyle={{ color: themeStyles.textPrimary, fontSize: 18 }}
                    />
                  </Col>
                  <Col xs={24} sm={8}>
                    <Statistic
                      title={<span style={{ color: themeStyles.textSecondary }}>Window</span>}
                      value={detail.window_days}
                      suffix="days"
                      valueStyle={{ color: themeStyles.textPrimary, fontSize: 18 }}
                    />
                  </Col>
                </Row>
                <Divider style={{ margin: '18px 0 14px', borderColor: themeStyles.borderColor }} />
                <Row gutter={[12, 10]} wrap>
                  <Col xs={24} sm={8}>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 2 }}>
                      LLM prompt tokens
                    </Text>
                    <Text style={{ color: themeStyles.textPrimary, fontSize: 14, fontWeight: 600 }}>
                      {(detail.aggregate?.total_prompt_tokens ?? 0).toLocaleString()}
                    </Text>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 2 }}>
                      LLM completion tokens
                    </Text>
                    <Text style={{ color: themeStyles.textPrimary, fontSize: 14, fontWeight: 600 }}>
                      {(detail.aggregate?.total_completion_tokens ?? 0).toLocaleString()}
                    </Text>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 2 }}>
                      Embedding tokens
                    </Text>
                    <Text style={{ color: themeStyles.textPrimary, fontSize: 14, fontWeight: 600 }}>
                      {(detail.aggregate?.total_embedding_tokens ?? 0).toLocaleString()}
                    </Text>
                  </Col>
                </Row>
              </div>
            </div>

            <div>
              <Text
                strong
                style={{
                  color: themeStyles.textPrimary,
                  fontSize: 15,
                  display: 'block',
                  marginBottom: 14,
                }}
              >
                By source
              </Text>
              <List
                size="small"
                bordered
                split
                dataSource={sourceUsageRows}
                locale={{ emptyText: 'No breakdown' }}
                renderItem={(row) => (
                  <List.Item style={{ padding: '14px 16px', borderColor: themeStyles.borderColor }}>
                    <Space direction="vertical" size={6} style={{ width: '100%' }}>
                      <Text strong style={{ color: themeStyles.textPrimary, fontSize: 14 }}>
                        {row.source}
                        <Text style={{ color: themeStyles.textSecondary, fontWeight: 400, marginLeft: 8 }}>
                          ${Number(row.cost_usd || 0).toFixed(6)}
                        </Text>
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.5 }}>
                        Events: {row.events || 0}
                        {' · '}
                        Prompt: {row.prompt_tokens || 0}
                        {' · '}
                        Completion: {row.completion_tokens || 0}
                        {' · '}
                        Embedding: {row.embedding_tokens || 0}
                      </Text>
                    </Space>
                  </List.Item>
                )}
              />
            </div>

            <div>
              <Text
                strong
                style={{
                  color: themeStyles.textPrimary,
                  fontSize: 15,
                  display: 'block',
                  marginBottom: 14,
                }}
              >
                Daily usage ({detail.window_days}d, IST)
              </Text>
              <div
                style={{
                  borderRadius: 10,
                  border: `1px solid ${themeStyles.borderColor}`,
                  overflow: 'hidden',
                  background: 'rgba(255, 255, 255, 0.02)',
                }}
              >
                <Table
                  size="small"
                  rowKey="day"
                  pagination={{
                    pageSize: 7,
                    showSizeChanger: false,
                    hideOnSinglePage: true,
                    size: 'small',
                  }}
                  dataSource={dailyUsageRows}
                  columns={dailyUsageColumns}
                  scroll={{ x: 640 }}
                  locale={{ emptyText: 'No usage in this window' }}
                  style={{ margin: 0 }}
                />
              </div>
            </div>

            <div>
              <Text
                strong
                style={{
                  color: themeStyles.textPrimary,
                  fontSize: 15,
                  display: 'block',
                  marginBottom: 14,
                }}
              >
                Recent events
              </Text>
              <List
                size="small"
                bordered
                split
                dataSource={detail.recent_events || []}
                locale={{ emptyText: 'No events' }}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: false,
                  hideOnSinglePage: true,
                  size: 'small',
                }}
                renderItem={(ev) => (
                  <List.Item style={{ padding: '14px 16px', borderColor: themeStyles.borderColor }}>
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.5 }}>
                        {formatToIST(ev.created_at)} IST
                        <span style={{ margin: '0 6px', opacity: 0.45 }}>|</span>
                        {ev.source}
                        <span style={{ margin: '0 6px', opacity: 0.45 }}>|</span>
                        {ev.kind}
                      </Text>
                      <Text style={{ color: themeStyles.textPrimary, fontSize: 14 }}>
                        ${Number(ev.cost_usd || 0).toFixed(6)}
                        {ev.model ? (
                          <Text type="secondary" style={{ marginLeft: 8, fontSize: 13 }}>
                            {ev.model}
                          </Text>
                        ) : null}
                      </Text>
                    </Space>
                  </List.Item>
                )}
              />
            </div>
          </Space>
        ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 220,
              padding: 32,
            }}
          >
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Text type="secondary" style={{ color: themeStyles.textSecondary }}>
                  No Data Found
                </Text>
              }
            />
          </div>
        )}
      </Drawer>
    </div>
  );
}

export default AdminCostMonitoring;
